import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { RootState } from "../../../../redux/store/store";

interface VisitDataChart {
  total_rows: number;
  data: Record<string, any>[];
  extracted_at: string;
  headers: string[];
}

interface VisitDataCharts {
  [medicationName: string]: VisitDataChart;
}

const VisitData = () => {
  const { singleMedicalPaOrder, isLoading } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const [visitDataCharts, setVisitDataCharts] = useState<VisitDataCharts>({});

  useEffect(() => {
    if (singleMedicalPaOrder?.auth_on_file) {
      const authOnFile = singleMedicalPaOrder.auth_on_file as any;
      const charts = authOnFile.visit_data_charts || {};
      setVisitDataCharts(charts);
    }
  }, [singleMedicalPaOrder]);

  const renderTable = (medicationName: string, chartData: VisitDataChart) => {
    const { headers, data } = chartData;

    return (
      <div key={medicationName} className="mb-6">
        <h4 className="text-md mb-3 font-semibold text-gray-800">
          {medicationName}
        </h4>
        <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  {headers.map((header) => (
                    <th
                      key={header}
                      className="border-b px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.map((row, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    {headers.map((header) => (
                      <td
                        key={header}
                        className="border-b px-4 py-3 text-sm text-gray-900"
                      >
                        {row[header] || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500">
            Total Rows: {chartData.total_rows} | Extracted At:{" "}
            {new Date(chartData.extracted_at).toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  const chartEntries = Object.entries(visitDataCharts);

  return (
    <div className="visit-data-container flex h-full flex-col">
      {isLoading ? (
        <LoaderMessage message={"Fetching Visit Data"} />
      ) : (
        <div className="h-full p-4">
          <h3 className="mb-4 text-lg font-semibold">Visit Data Charts</h3>
          {chartEntries.length > 0 ? (
            <div className="max-h-full space-y-6 overflow-y-auto">
              {chartEntries.map(([medicationName, chartData]) =>
                renderTable(medicationName, chartData),
              )}
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center rounded-lg bg-gray-50">
              <p className="text-center text-gray-500">
                No visit data charts found
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitData;
